import { useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, ChevronDown, Phone, Mail, CalendarCheck, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useI18n } from "@/lib/i18n";
import LanguageToggle from "./LanguageToggle";
import RegionSwitcher from "./RegionSwitcher";
import mtliLogo from "@/assets/mtli-logo.png";

// Nav mirrors the five divisions: each division expands to its key specialty
// pages so visitors can route themselves from the very first click. The full
// division hubs carry the complete page lists.
const services = [
  {
    name: "ICI Construction", nameFr: "Construction ICI", href: "/services/construction-general-contracting",
    children: [
      { name: "Warehouse Construction", nameFr: "Construction d'entrepôts", href: "/services/construction-general-contracting/warehouse-construction" },
      { name: "Design-Build", nameFr: "Conception-construction", href: "/services/construction-general-contracting/design-build" },
      { name: "Construction Management", nameFr: "Gestion de construction", href: "/services/construction-general-contracting/construction-management" },
      { name: "Cold Storage Construction", nameFr: "Entreposage frigorifique", href: "/services/construction-general-contracting/cold-storage-construction" },
      { name: "Office Buildouts", nameFr: "Bureaux et aménagements", href: "/services/construction-general-contracting/office-buildouts" },
      { name: "Restoration & Repairs", nameFr: "Restauration et réparations", href: "/services/construction-general-contracting/building-restoration-repairs" },
    ],
  },
  {
    name: "Storage Solutions & Automation", nameFr: "Solutions d'entreposage et automatisation", href: "/services/racking-automation",
    children: [
      { name: "Storage & Racking", nameFr: "Rayonnage et entreposage", href: "/services/storage-racking" },
      { name: "Warehouse Automation", nameFr: "Automatisation d'entrepôt", href: "/services/warehouse-automation" },
      { name: "Manufacturing Automation & Robotics", nameFr: "Automatisation manufacturière et robotique", href: "/services/warehouse-automation/manufacturing-automation-robotics" },
      { name: "AGV & AMR Systems", nameFr: "Systèmes AGV et AMR", href: "/services/warehouse-automation/agv-systems" },
      { name: "Mezzanines & Platforms", nameFr: "Mezzanines et plateformes", href: "/services/storage-racking/mezzanines-platforms" },
      { name: "New & Used Equipment", nameFr: "Équipement neuf et usagé", href: "/services/storage-racking/used-racking-equipment" },
      { name: "In-Stock Equipment Shop", nameFr: "Équipement en stock", href: "/products" },
    ],
  },
  {
    name: "Installation & Maintenance", nameFr: "Installation et maintenance", href: "/services/installations",
    children: [
      { name: "Racking Installation", nameFr: "Installation de rayonnage", href: "/services/installations/racking-installation" },
      { name: "Conveyor & Sorters", nameFr: "Convoyeurs et trieurs", href: "/services/installations/conveyor-installation" },
      { name: "Mechanical & Electrical", nameFr: "Mécanique et électrique", href: "/services/installations/mechanical-installation" },
      { name: "Steel Erection & Rigging", nameFr: "Montage d'acier et gréage", href: "/services/installations/steel-erection" },
      { name: "Large DC Build-Outs", nameFr: "Aménagements de grands CD", href: "/services/installations/large-dc-buildouts" },
      { name: "National Rollouts", nameFr: "Déploiements nationaux", href: "/services/installations/national-rollout-programs" },
      { name: "Skilled Trades & Labour", nameFr: "Métiers et main-d'œuvre", href: "/services/installations/skilled-trades-labour" },
    ],
  },
  {
    name: "Facility Maintenance", nameFr: "Maintenance des installations", href: "/services/facility-maintenance",
    children: [
      { name: "Preventative Programs", nameFr: "Programmes préventifs", href: "/services/facility-maintenance/preventative-maintenance-programs" },
      { name: "24/7 Emergency Repairs", nameFr: "Urgences 24/7", href: "/services/facility-maintenance/emergency-repairs" },
      { name: "Rack Repairs & Protection", nameFr: "Réparations de rayonnage", href: "/services/facility-maintenance/rack-repairs-protection" },
      { name: "Multi-Site FM Programs", nameFr: "Programmes FM multi-sites", href: "/services/facility-maintenance/multi-site-fm-programs" },
    ],
  },
  {
    name: "Moving & Liquidation", nameFr: "Déménagement et liquidation", href: "/services/relocations",
    children: [
      { name: "Warehouse Relocation", nameFr: "Relocalisation d'entrepôt", href: "/services/relocations/warehouse-relocation" },
      { name: "Decommissioning", nameFr: "Déclassement", href: "/services/relocations/facility-decommissioning" },
      { name: "Asset Recovery & Liquidation", nameFr: "Récupération d'actifs", href: "/services/relocations/asset-recovery-liquidation" },
      { name: "Plant Shutdowns", nameFr: "Fermetures d'usines", href: "/services/relocations/plant-shutdown-services" },
      { name: "Sell Us Your Equipment", nameFr: "Vendez-nous votre équipement", href: "/sell-your-equipment" },
    ],
  },
  {
    name: "Electrical & Controls", nameFr: "Électricité et contrôles", href: "/services/electrical-automation-services",
    children: [
      { name: "Electrical Maintenance", nameFr: "Maintenance électrique", href: "/services/facility-maintenance/electrical-maintenance" },
    ],
  },
];

const industries = [
  { name: "Warehousing & Distribution", nameFr: "Entreposage et distribution", href: "/industries/warehousing-distribution" },
  { name: "3PL & Logistics", nameFr: "3PL et logistique", href: "/industries/3pl-logistics" },
  { name: "E-Commerce Fulfillment", nameFr: "Exécution e-commerce", href: "/industries/ecommerce-fulfillment" },
  { name: "Manufacturing", nameFr: "Fabrication", href: "/industries/manufacturing" },
  { name: "Food & Beverage", nameFr: "Alimentation et boissons", href: "/industries/food-beverage" },
  { name: "Cold Storage", nameFr: "Entreposage frigorifique", href: "/industries/cold-storage" },
  { name: "Pharmaceutical", nameFr: "Pharmaceutique", href: "/industries/pharmaceutical" },
  { name: "Automotive", nameFr: "Automobile", href: "/industries/automotive" },
];


const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileDivision, setMobileDivision] = useState<string | null>(null);
  const { config, isFrench, t, getLocalePath, isCanada } = useI18n();

  const getServiceName = (service: typeof services[0]) => isFrench && service.nameFr ? service.nameFr : service.name;
  const getIndustryName = (industry: typeof industries[0]) => isFrench && industry.nameFr ? industry.nameFr : industry.name;

  return (
    <header className="sticky top-0 z-50 w-full bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border">
      {/* Primary CTA banner, the single action every page drives toward. */}
      <div className="bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 py-2 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-center text-sm">
          <span className="font-medium">Partner with MTLI on your next project today</span>
          <Link
            to={getLocalePath("/book-a-call")}
            className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3.5 py-1 font-semibold hover:bg-white/25 transition-colors"
          >
            <CalendarCheck className="h-4 w-4" />
            Book a Call
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>

      {/* Top bar */}
      <div className="hidden md:block bg-secondary text-secondary-foreground">
        <div className="container mx-auto px-4 py-2 flex justify-end items-center text-sm">
          <div className="flex items-center gap-4">
            <span>{config.tagline}</span>
            <RegionSwitcher />
            {isCanada && <LanguageToggle />}
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <div className="container mx-auto px-4">
        <nav className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to={getLocalePath("/")} className="flex shrink-0 items-center gap-3">
            <img src={mtliLogo} alt="MTLI Group of Companies" className="h-12 w-auto" loading="lazy" decoding="async" />
            <span className="hidden 2xl:block whitespace-nowrap text-[10px] uppercase tracking-[0.15em] text-muted-foreground leading-tight font-medium">
              Manufacturing • Technology<br />Logistics • Infrastructure
            </span>
          </Link>

          {/* Desktop Navigation - Optimized for conversion flow */}
          <div className="hidden lg:flex items-center gap-1 ml-6 xl:ml-8">

            {/* Services Dropdown - Primary action */}
            <div 
              className="relative"
              onMouseEnter={() => setActiveDropdown("services")}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <button className="flex items-center gap-1 px-4 py-2 text-sm font-medium hover:text-primary transition-colors">
                {t('nav.services')}
                <ChevronDown className={cn("h-4 w-4 transition-transform", activeDropdown === "services" && "rotate-180")} />
              </button>
              {activeDropdown === "services" && (
                <div className="absolute top-full left-0 pt-2 z-50">
                  {/* Division mega-menu: every division opens with its key pages,
                      so visitors (and crawlers) get the site map from any page. */}
                  <div className="bg-background border border-border rounded-lg shadow-lg p-5 w-[640px] xl:w-[880px]">
                    <Link
                      to={getLocalePath("/services")}
                      className="block text-sm font-semibold text-primary hover:underline mb-4"
                      onClick={() => setActiveDropdown(null)}
                    >
                      {t('nav.allServices')}
                    </Link>
                    <div className="grid grid-cols-2 xl:grid-cols-3 gap-x-8 gap-y-6">
                      {services.map((service) => (
                        <div key={service.href}>
                          <Link
                            to={getLocalePath(service.href)}
                            className="block text-sm font-bold text-foreground hover:text-primary transition-colors mb-2"
                            onClick={() => setActiveDropdown(null)}
                          >
                            {getServiceName(service)}
                          </Link>
                          <div className="space-y-1">
                            {service.children.map((child) => (
                              <Link
                                key={child.href}
                                to={getLocalePath(child.href)}
                                className="block text-[13px] text-muted-foreground hover:text-primary transition-colors py-0.5"
                                onClick={() => setActiveDropdown(null)}
                              >
                                {isFrench && child.nameFr ? child.nameFr : child.name}
                              </Link>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Industries Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setActiveDropdown("industries")}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <button className="flex items-center gap-1 px-4 py-2 text-sm font-medium hover:text-primary transition-colors">
                {t('nav.industries')}
                <ChevronDown className={cn("h-4 w-4 transition-transform", activeDropdown === "industries" && "rotate-180")} />
              </button>
              {activeDropdown === "industries" && (
                <div className="absolute top-full left-0 w-64 pt-2 z-50">
                  <div className="bg-background border border-border rounded-lg shadow-lg py-2">
                    <Link 
                      to={getLocalePath("/industries")} 
                      className="block px-4 py-2 text-sm font-semibold text-primary hover:bg-muted"
                      onClick={() => setActiveDropdown(null)}
                    >
                      {t('nav.allIndustries')}
                    </Link>
                    <div className="border-t border-border my-1" />
                    {industries.map((industry) => (
                      <Link
                        key={industry.href}
                        to={getLocalePath(industry.href)}
                        className="block px-4 py-2 text-sm hover:bg-muted hover:text-primary transition-colors"
                        onClick={() => setActiveDropdown(null)}
                      >
                        {getIndustryName(industry)}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Projects - Social proof */}
            <Link to={getLocalePath("/projects")} className="px-4 py-2 text-sm font-medium hover:text-primary transition-colors">
              {t('nav.projects')}
            </Link>

            {/* Locations - Regional relevance */}
            <Link to={getLocalePath("/locations")} className="px-4 py-2 text-sm font-medium hover:text-primary transition-colors">
              {t('nav.locations')}
            </Link>

            {/* About - Trust building */}
            <Link to={getLocalePath("/about")} className="px-4 py-2 text-sm font-medium hover:text-primary transition-colors">
              {t('nav.about')}
            </Link>

            {/* Resources - Lead magnet */}
            <Link to={getLocalePath("/resources")} className="px-4 py-2 text-sm font-medium hover:text-primary transition-colors">
              {t('nav.resources')}
            </Link>

            {/* Partnerships - Grow the network */}
            <Link to={getLocalePath("/partnerships")} className="px-4 py-2 text-sm font-medium hover:text-primary transition-colors">
              Partnerships
            </Link>

            {/* Careers - Secondary */}
            <Link to={getLocalePath("/careers")} className="px-4 py-2 text-sm font-medium hover:text-primary transition-colors">
              {t('nav.careers')}
            </Link>

            {/* Blog */}
            <Link to={getLocalePath("/blog")} className="px-4 py-2 text-sm font-medium hover:text-primary transition-colors">
              {isFrench ? "Blogue" : "Blog"}
            </Link>
          </div>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-3">
            <Button variant="outline" asChild>
              <Link to={getLocalePath("/contact")}>{t('nav.contact')}</Link>
            </Button>
            <Button asChild>
              <Link to={getLocalePath("/book-a-call")}>Book a Call</Link>
            </Button>
          </div>

          {/* Mobile menu button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </nav>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-border py-4">
            <div className="flex flex-col gap-2">
              <Link to={getLocalePath("/")} className="px-4 py-2 font-medium hover:text-primary" onClick={() => setMobileMenuOpen(false)}>{isFrench ? "Accueil" : "Home"}</Link>
              {/* Services: division accordion so mobile users can route per division */}
              <Link to={getLocalePath("/services")} className="px-4 py-2 font-medium hover:text-primary" onClick={() => setMobileMenuOpen(false)}>{t('nav.services')}</Link>
              <div className="flex flex-col">
                {services.map((service) => (
                  <div key={service.href}>
                    <div className="flex items-center justify-between pr-4">
                      <Link
                        to={getLocalePath(service.href)}
                        className="flex-1 pl-8 pr-2 py-2 text-sm font-medium text-foreground/85 hover:text-primary"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {getServiceName(service)}
                      </Link>
                      <button
                        aria-label={`Expand ${service.name}`}
                        className="p-2 text-muted-foreground hover:text-primary"
                        onClick={() => setMobileDivision(mobileDivision === service.href ? null : service.href)}
                      >
                        <ChevronDown className={cn("h-4 w-4 transition-transform", mobileDivision === service.href && "rotate-180")} />
                      </button>
                    </div>
                    {mobileDivision === service.href && (
                      <div className="flex flex-col pb-1">
                        {service.children.map((child) => (
                          <Link
                            key={child.href}
                            to={getLocalePath(child.href)}
                            className="pl-12 pr-4 py-1.5 text-sm text-muted-foreground hover:text-primary"
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            {isFrench && child.nameFr ? child.nameFr : child.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <Link to={getLocalePath("/industries")} className="px-4 py-2 font-medium hover:text-primary" onClick={() => setMobileMenuOpen(false)}>{t('nav.industries')}</Link>
              <Link to={getLocalePath("/locations")} className="px-4 py-2 font-medium hover:text-primary" onClick={() => setMobileMenuOpen(false)}>{t('nav.locations')}</Link>
              <Link to={getLocalePath("/projects")} className="px-4 py-2 font-medium hover:text-primary" onClick={() => setMobileMenuOpen(false)}>{t('nav.projects')}</Link>
              <Link to={getLocalePath("/about")} className="px-4 py-2 font-medium hover:text-primary" onClick={() => setMobileMenuOpen(false)}>{t('nav.about')}</Link>
              <Link to={getLocalePath("/resources")} className="px-4 py-2 font-medium hover:text-primary" onClick={() => setMobileMenuOpen(false)}>{t('nav.resources')}</Link>
              <Link to={getLocalePath("/partnerships")} className="px-4 py-2 font-medium hover:text-primary" onClick={() => setMobileMenuOpen(false)}>Partnerships</Link>
              <Link to={getLocalePath("/careers")} className="px-4 py-2 font-medium hover:text-primary" onClick={() => setMobileMenuOpen(false)}>{t('nav.careers')}</Link>
              <Link to={getLocalePath("/blog")} className="px-4 py-2 font-medium hover:text-primary" onClick={() => setMobileMenuOpen(false)}>{isFrench ? "Blogue" : "Blog"}</Link>
              
              {/* Region & Language Switchers */}
              <div className="flex items-center gap-4 px-4 py-2 border-t border-border mt-2 pt-4">
                <RegionSwitcher />
                {isCanada && <LanguageToggle />}
              </div>
              
              <div className="flex flex-col gap-2 px-4 pt-4">
                <Button variant="outline" asChild className="w-full">
                  <Link to={getLocalePath("/contact")} onClick={() => setMobileMenuOpen(false)}>{t('nav.contact')}</Link>
                </Button>
                <Button asChild className="w-full">
                  <Link to={getLocalePath("/book-a-call")} onClick={() => setMobileMenuOpen(false)}>Book a Call</Link>
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
